import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchUserConnectionStatus,
  storeTwitchToken,
  storeYouTubeToken,
} from "../services/stream";
import { set } from "date-fns";
import { dynamicWebUrl } from "../Api/configs";

// Define interfaces for type safety
interface ConnectionStatus {
  YouTube: string;
  Twitch: string;
  Squadifi: string;
  twitchUsername: string | null;
  youtubeUsername: string | null;
  squadifiUsername: string | null;
}

interface LoadingState {
  YouTube: boolean;
  Twitch: boolean;
}

const clientId =
  "60438075975-tagi8drra8u1qk3v7phhvtogj585p6i6.apps.googleusercontent.com";
const youtubeScope =
  "https://www.googleapis.com/auth/youtube https://www.googleapis.com/auth/youtube.force-ssl";
const twitchClientId = "3i2fof24aphei7mvzp8l0d3xt0qhk0";
const twitchScope =
  "user:read:email channel:manage:broadcast channel:read:stream_key channel:manage:schedule";

export const developUrl = dynamicWebUrl;

export const useStreamingOAuth = (
  accessToken: string,
  redirectUriPath: string
) => {
  const [isYouTubeConnected, setIsYouTubeConnected] = useState<boolean>(false);
  const [isTwitchConnected, setIsTwitchConnected] = useState<boolean>(false);
  const [twitchUsername, setTwitchUsername] = useState<string | null>(null);
  const [youtubeUsername, setYoutubeUsername] = useState<string | null>(null);
  const [squadifiUsername, setSquadifiUsername] = useState<string | null>(null);

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    YouTube: false,
    Twitch: false,
  });

  const actualRedirectUri = `${developUrl}/settings?tab=streamSettings`;

  const fetchStatus = async (silent = false) => {
    try {
      const response = await fetchUserConnectionStatus(accessToken);
      if (response.statusCode === 200) {
        const newYouTubeConnected = response.body.YouTube === "Connected";
        const newTwitchConnected = response.body.Twitch === "Connected";

        if (isYouTubeConnected !== newYouTubeConnected) {
          setIsYouTubeConnected(newYouTubeConnected);
        }
        if (isTwitchConnected !== newTwitchConnected) {
          setIsTwitchConnected(newTwitchConnected);
        }
        if (twitchUsername !== response.body.twitchUsername) {
          setTwitchUsername(response.body.twitchUsername);
        }
        if (youtubeUsername !== response.body.youtubeUsername) {
          setYoutubeUsername(response.body.youtubeUsername);
        }
        if (squadifiUsername !== response.body.squadifiUsername) {
          setSquadifiUsername(response.body.squadifiUsername);
        }
      }
    } catch (error) {
      if (!silent) {
        console.error("Error fetching platform status:", error);
      }
    } finally {
      if (isInitialLoading) {
        setIsInitialLoading(false);
      }
    }
  };

  const redirectToYouTubeOAuth = () => {
    localStorage.setItem("oauthReturnUrl", redirectUriPath);
    const authUrl = `https://accounts.google.com/o/oauth2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      actualRedirectUri
    )}&response_type=code&scope=${encodeURIComponent(
      youtubeScope
    )}&access_type=offline&prompt=consent&include_granted_scopes=true`;
    window.location.href = authUrl;
  };

  const redirectToTwitchOAuth = () => {
    localStorage.setItem("oauthReturnUrl", redirectUriPath);
    const authUrl = `https://id.twitch.tv/oauth2/authorize?client_id=${twitchClientId}&redirect_uri=${encodeURIComponent(
      actualRedirectUri
    )}&response_type=code&scope=${encodeURIComponent(twitchScope)}`;
    window.location.href = authUrl;
  };

  useEffect(() => {
    const extractAndStoreToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get("code");

      // Skip if no auth code present
      if (!authCode) {
        return;
      }

      // Check if we have a stored event redirect
      const redirectEventID = localStorage.getItem("oauthRedirectEventID");
      const redirectTab = localStorage.getItem("oauthRedirectTab");

      // Check if we have a stored return URL (new generic method)
      const returnUrl = localStorage.getItem("oauthReturnUrl");

      if (authCode?.length === 73) {
        setLoadingState((prev) => ({ ...prev, YouTube: true }));
        try {
          const response = await storeYouTubeToken(
            authCode,
            accessToken,
            actualRedirectUri
          );
          if (response.statusCode === 200) {
            toast.success("YouTube connected successfully!");
            await fetchStatus(true);

            // Redirect back using stored generic return URL if available
            if (returnUrl) {
              localStorage.removeItem("oauthReturnUrl");
              window.location.href = returnUrl;
              return;
            }

            // Fallback: Redirect back to event using stored context (legacy/specific)
            if (redirectEventID && redirectTab) {
              localStorage.removeItem("oauthRedirectEventID");
              localStorage.removeItem("oauthRedirectTab");

              // Use window.location to ensure full navigation
              const redirectUrl = `/events/ongoing?eventID=${redirectEventID}&tab=${redirectTab}`;
              window.location.href = redirectUrl;
            }
          } else {
            toast.error(
              response.body.message || "Failed to connect to YouTube."
            );
          }
        } catch (error) {
          console.error("Error storing YouTube token:", error);
          toast.error("Failed to connect to YouTube.");
        } finally {
          setLoadingState((prev) => ({ ...prev, YouTube: false }));
        }
      } else if (authCode?.length === 30) {
        setLoadingState((prev) => ({ ...prev, Twitch: true }));
        try {
          const response = await storeTwitchToken(
            authCode,
            accessToken,
            actualRedirectUri
          );
          if (response.statusCode === 200) {
            toast.success("Twitch connected successfully!");
            await fetchStatus(true);

            // Redirect back using stored generic return URL if available
            if (returnUrl) {
              localStorage.removeItem("oauthReturnUrl");
              window.location.href = returnUrl;
              return;
            }

            // Fallback: Redirect back to event using stored context (legacy/specific)
            if (redirectEventID && redirectTab) {
              localStorage.removeItem("oauthRedirectEventID");
              localStorage.removeItem("oauthRedirectTab");

              // Use window.location to ensure full navigation
              const redirectUrl = `/events/ongoing?eventID=${redirectEventID}&tab=${redirectTab}`;
              window.location.href = redirectUrl;
            }
          } else {
            toast.error(
              response.body.message || "Failed to connect to Twitch."
            );
          }
        } catch (error) {
          console.error("Error storing Twitch token:", error);
          toast.error("Failed to connect to Twitch.");
        } finally {
          setLoadingState((prev) => ({ ...prev, Twitch: false }));
        }
      }
    };

    if (accessToken) {
      extractAndStoreToken();
    }
  }, [accessToken, window.location.search]);

  useEffect(() => {
    if (accessToken) {
      fetchStatus(true);
    }
  }, [accessToken]);

  return {
    isYouTubeConnected,
    isTwitchConnected,
    redirectToYouTubeOAuth,
    redirectToTwitchOAuth,
    loadingState,
    isInitialLoading,
    twitchUsername,
    youtubeUsername,
    squadifiUsername,
    fetchStatus,
  };
};
